/**
 * Grok Imagine Video Prompt Management
 * Admin dashboard for managing prompt variations (A/B),
 * viewing scene analysis analytics, and testing prompts.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Collapse,
  CircularProgress,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  PlayArrow as ActivateIcon,
  RestartAlt as ResetIcon,
  Science as TestIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AdminEditDialogShell from '../shared/AdminEditDialogShell';
import PromptField from '../shared/PromptField';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { apiService } from '../../services/api';
import ModelDefaultsBar from './styles/ModelDefaultsBar';

// ─── Types ───────────────────────────────────────────────────────────
interface PromptVariation {
  id: number;
  name: string;
  description: string;
  variation_type: string;
  scene_analysis_system_prompt: string;
  video_generation_prompt: string;
  is_active: boolean;
  video_duration: number;
  video_resolution: string;
  video_aspect_ratio: string;
  created_by: string;
  updated_by: string;
  last_tested_at: string | null;
  test_result_url: string | null;
  usage_count: number;
  avg_confidence_score: number | null;
  usage_count_computed: number;
  created_at: string;
  updated_at: string;
}

interface AnalyticsRow {
  variation_type: string;
  total_analyses: number;
  avg_confidence: number | null;
  passed_count: number;
  failed_count: number;
  avg_analysis_time: number | null;
  sensitive_count: number;
}

interface SceneLogRow {
  id: string;
  user_id: string;
  chapter_id: string;
  prompt_id: number;
  variation_type: string;
  source_photo_url: string;
  scene_description: string;
  people_description: string;
  setting_description: string;
  emotional_tone: string;
  animation_prompt: string;
  animation_style: string;
  confidence_score: number;
  passed_quality_gate: boolean;
  contains_sensitive_content: boolean;
  sensitive_content_flags: string[];
  analysis_time_ms: number;
  model_used: string;
  prompt_name: string;
  created_at: string;
}

// ─── Component ───────────────────────────────────────────────────────
const GrokImaginePromptsManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [tabIndex, setTabIndex] = useState(0);

  // Prompt Variations state
  const [variations, setVariations] = useState<PromptVariation[]>([]);
  const [loadingVariations, setLoadingVariations] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingVariation, setEditingVariation] = useState<PromptVariation | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Test state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testVariationType, setTestVariationType] = useState('');
  const [testImageUrl, setTestImageUrl] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  // Analytics state
  const [analytics, setAnalytics] = useState<{ byVariation: AnalyticsRow[]; totals: any } | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // Scene Log state
  const [sceneLog, setSceneLog] = useState<SceneLogRow[]>([]);
  const [sceneLogTotal, setSceneLogTotal] = useState(0);
  const [sceneLogPage, setSceneLogPage] = useState(0);
  const [sceneLogLimit] = useState(25);
  const [sceneLogFilter, setSceneLogFilter] = useState('');
  const [loadingSceneLog, setLoadingSceneLog] = useState(true);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // ─── Data Fetching ─────────────────────────────────────────────────
  const fetchVariations = useCallback(async () => {
    setLoadingVariations(true);
    try {
      const data = await apiService.getGrokImagineVariations();
      setVariations(data || []);
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: 'Failed to load prompt variations' }));
    } finally {
      setLoadingVariations(false);
    }
  }, [dispatch]);

  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const data = await apiService.getGrokImagineAnalytics();
      setAnalytics(data || null);
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: 'Failed to load analytics' }));
    } finally {
      setLoadingAnalytics(false);
    }
  }, [dispatch]);

  const fetchSceneLog = useCallback(async () => {
    setLoadingSceneLog(true);
    try {
      const data = await apiService.getGrokImagineSceneLog({
        page: sceneLogPage,
        limit: sceneLogLimit,
        variation_type: sceneLogFilter || undefined,
      });
      setSceneLog(data?.rows || []);
      setSceneLogTotal(data?.total || 0);
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: 'Failed to load scene log' }));
    } finally {
      setLoadingSceneLog(false);
    }
  }, [dispatch, sceneLogPage, sceneLogLimit, sceneLogFilter]);

  useEffect(() => {
    fetchVariations();
  }, [fetchVariations]);

  useEffect(() => {
    if (tabIndex === 1) fetchAnalytics();
  }, [tabIndex, fetchAnalytics]);

  useEffect(() => {
    if (tabIndex === 2) fetchSceneLog();
  }, [tabIndex, fetchSceneLog]);

  // Auto-refresh analytics every 30s when on analytics tab
  useEffect(() => {
    if (tabIndex !== 1) return;
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [tabIndex, fetchAnalytics]);

  // ─── Handlers ──────────────────────────────────────────────────────
  const handleActivate = async (variationType: string) => {
    try {
      await apiService.activateGrokImagineVariation(variationType);
      dispatch(addNotification({ type: 'success', message: `Variation ${variationType} activated` }));
      fetchVariations();
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: 'Failed to activate variation' }));
    }
  };

  const handleOpenEdit = (variation: PromptVariation) => {
    setEditingVariation(variation);
    setEditForm({
      name: variation.name,
      description: variation.description,
      scene_analysis_system_prompt: variation.scene_analysis_system_prompt,
      video_generation_prompt: variation.video_generation_prompt,
      video_duration: variation.video_duration,
      video_resolution: variation.video_resolution,
      video_aspect_ratio: variation.video_aspect_ratio,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingVariation) return;
    setSaving(true);
    try {
      await apiService.updateGrokImagineVariation(editingVariation.variation_type, editForm);
      dispatch(addNotification({ type: 'success', message: `Variation ${editingVariation.variation_type} updated` }));
      setEditDialogOpen(false);
      fetchVariations();
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: 'Failed to save changes' }));
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    try {
      await apiService.resetGrokImagineDefaults();
      dispatch(addNotification({ type: 'success', message: 'All variations reset to defaults' }));
      setResetDialogOpen(false);
      fetchVariations();
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: 'Failed to reset defaults' }));
    }
  };

  const handleTest = async () => {
    if (!testImageUrl) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const data = await apiService.testGrokImaginePrompt({
        image_url: testImageUrl,
        variation_type: testVariationType || undefined,
      });
      setTestResult(data);
      dispatch(addNotification({ type: 'success', message: 'Test completed' }));
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: error.message || 'Test failed' }));
    } finally {
      setTestLoading(false);
    }
  };

  // ─── Tab 1: Prompt Variations ──────────────────────────────────────
  const renderVariationsTab = () => {
    if (loadingVariations) {
      return (
        <Grid container spacing={3}>
          {[0, 1].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6">Prompt Variations (A / B)</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ResetIcon />}
              onClick={() => setResetDialogOpen(true)}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="outlined"
              startIcon={<TestIcon />}
              onClick={() => {
                setTestVariationType('');
                setTestImageUrl('');
                setTestResult(null);
                setTestDialogOpen(true);
              }}
            >
              Test Prompt
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {variations.filter(v => v.variation_type !== 'C').map((v) => (
            <Grid item xs={12} md={6} key={v.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: 'success.main',
                  borderWidth: 2,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" component="div">
                      {v.variation_type}: {v.name}
                    </Typography>
                    <Chip label="Active" color="success" size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {v.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={`${v.video_duration}s`} size="small" variant="outlined" />
                    <Chip label={v.video_resolution} size="small" variant="outlined" />
                    <Chip label={v.video_aspect_ratio} size="small" variant="outlined" />
                  </Box>

                  <Typography variant="caption" display="block" color="text.secondary">
                    Avg Confidence: {v.avg_confidence_score != null ? Number(v.avg_confidence_score).toFixed(2) : 'N/A'}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Usage Count: {v.usage_count_computed || v.usage_count || 0}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    System Prompt: {v.scene_analysis_system_prompt.length} chars
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Video Prompt: {v.video_generation_prompt.length} chars
                  </Typography>
                  {v.last_tested_at && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Last Tested: {new Date(v.last_tested_at).toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenEdit(v)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<TestIcon />}
                    onClick={() => {
                      setTestVariationType(v.variation_type);
                      setTestImageUrl('');
                      setTestResult(null);
                      setTestDialogOpen(true);
                    }}
                  >
                    Test
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // ─── Tab 2: Analytics ──────────────────────────────────────────────
  const renderAnalyticsTab = () => {
    if (loadingAnalytics) {
      return <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />;
    }

    if (!analytics) {
      return <Alert severity="info">No analytics data available yet. Scene analyses will appear here once the pipeline processes photos.</Alert>;
    }

    const { totals, byVariation } = analytics;
    const passRate = totals.total_analyses > 0
      ? ((Number(totals.passed_count) / Number(totals.total_analyses)) * 100).toFixed(1)
      : '0';
    const sensitiveRate = totals.total_analyses > 0
      ? ((Number(totals.sensitive_count) / Number(totals.total_analyses)) * 100).toFixed(1)
      : '0';

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6">Scene Analysis Analytics</Typography>
          <Button startIcon={<RefreshIcon />} onClick={fetchAnalytics} size="small">
            Refresh
          </Button>
        </Box>

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4">{Number(totals.total_analyses).toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Total Analyses</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4">{totals.avg_confidence != null ? Number(totals.avg_confidence).toFixed(2) : 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary">Avg Confidence</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">{passRate}%</Typography>
                <Typography variant="body2" color="text.secondary">Quality Gate Pass</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">{sensitiveRate}%</Typography>
                <Typography variant="body2" color="text.secondary">Sensitive Content</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Per-variation breakdown */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Variation</TableCell>
                <TableCell align="right">Analyses</TableCell>
                <TableCell align="right">Avg Confidence</TableCell>
                <TableCell align="right">Passed</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell align="right">Avg Time (ms)</TableCell>
                <TableCell align="right">Sensitive</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byVariation.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No data yet</TableCell>
                </TableRow>
              ) : (
                byVariation.map((row) => (
                  <TableRow key={row.variation_type}>
                    <TableCell>
                      <Chip label={`Variation ${row.variation_type}`} size="small" />
                    </TableCell>
                    <TableCell align="right">{Number(row.total_analyses).toLocaleString()}</TableCell>
                    <TableCell align="right">{row.avg_confidence != null ? Number(row.avg_confidence).toFixed(3) : 'N/A'}</TableCell>
                    <TableCell align="right">{Number(row.passed_count).toLocaleString()}</TableCell>
                    <TableCell align="right">{Number(row.failed_count).toLocaleString()}</TableCell>
                    <TableCell align="right">{row.avg_analysis_time != null ? Number(row.avg_analysis_time).toLocaleString() : 'N/A'}</TableCell>
                    <TableCell align="right">{Number(row.sensitive_count).toLocaleString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ─── Tab 3: Scene Analysis Log ─────────────────────────────────────
  const renderSceneLogTab = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Scene Analysis Log</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Variation</InputLabel>
              <Select
                value={sceneLogFilter}
                label="Variation"
                onChange={(e) => {
                  setSceneLogFilter(e.target.value);
                  setSceneLogPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="A">A - Still & Tender</MenuItem>
                <MenuItem value="B">B - Alive & Moving</MenuItem>
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={fetchSceneLog} size="small">
              Refresh
            </Button>
          </Box>
        </Box>

        {loadingSceneLog ? (
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        ) : sceneLog.length === 0 ? (
          <Alert severity="info">No scene analyses recorded yet.</Alert>
        ) : (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>Created</TableCell>
                    <TableCell>Variation</TableCell>
                    <TableCell align="right">Confidence</TableCell>
                    <TableCell>Quality Gate</TableCell>
                    <TableCell>Style</TableCell>
                    <TableCell>Sensitive</TableCell>
                    <TableCell align="right">Time (ms)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sceneLog.map((row) => (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expandedRow === row.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip label={row.variation_type} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={
                              row.confidence_score >= 0.7
                                ? 'success.main'
                                : row.confidence_score >= 0.6
                                ? 'warning.main'
                                : 'error.main'
                            }
                          >
                            {Number(row.confidence_score).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.passed_quality_gate ? 'Pass' : 'Fail'}
                            color={row.passed_quality_gate ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{row.animation_style || 'N/A'}</TableCell>
                        <TableCell>
                          {row.contains_sensitive_content ? (
                            <Chip label="Yes" color="warning" size="small" variant="outlined" />
                          ) : (
                            'No'
                          )}
                        </TableCell>
                        <TableCell align="right">{row.analysis_time_ms?.toLocaleString() || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, borderBottom: expandedRow === row.id ? undefined : 'none' }}>
                          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>Scene Description</Typography>
                                  <Typography variant="body2" sx={{ mb: 2 }}>{row.scene_description || 'N/A'}</Typography>
                                  <Typography variant="subtitle2" gutterBottom>People Description</Typography>
                                  <Typography variant="body2" sx={{ mb: 2 }}>{row.people_description || 'N/A'}</Typography>
                                  <Typography variant="subtitle2" gutterBottom>Setting Description</Typography>
                                  <Typography variant="body2">{row.setting_description || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>Emotional Tone</Typography>
                                  <Typography variant="body2" sx={{ mb: 2 }}>{row.emotional_tone || 'N/A'}</Typography>
                                  <Typography variant="subtitle2" gutterBottom>Animation Prompt</Typography>
                                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{row.animation_prompt || 'N/A'}</Typography>
                                  {row.sensitive_content_flags && row.sensitive_content_flags.length > 0 && (
                                    <Box>
                                      <Typography variant="subtitle2" gutterBottom>Sensitive Flags</Typography>
                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {row.sensitive_content_flags.map((flag, i) => (
                                          <Chip key={i} label={flag} size="small" color="warning" variant="outlined" />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                                    Model: {row.model_used} | Prompt: {row.prompt_name || 'N/A'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sceneLogTotal}
              page={sceneLogPage}
              onPageChange={(_, newPage) => setSceneLogPage(newPage)}
              rowsPerPage={sceneLogLimit}
              rowsPerPageOptions={[25]}
            />
          </Paper>
        )}
      </Box>
    );
  };

  // ─── Dialogs ───────────────────────────────────────────────────────

  const renderEditDialog = () => (
    <AdminEditDialogShell
      open={editDialogOpen}
      onClose={() => setEditDialogOpen(false)}
      accentColor="#7D1935"
      typeInitial={editingVariation?.variation_type || 'A'}
      title={`Edit Grok Imagine Video — Variation ${editingVariation?.variation_type}: ${editingVariation?.name || ''}`}
      subtitle="Video generation · 2-pass pipeline · changes live within 5 minutes"
      onSave={handleSaveEdit}
      isSaving={saving}
    >
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              fullWidth
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Description"
              fullWidth
              value={editForm.description || ''}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Video Duration (s)"
              type="number"
              fullWidth
              inputProps={{ min: 1, max: 8 }}
              value={editForm.video_duration || ''}
              onChange={(e) => setEditForm({ ...editForm, video_duration: parseInt(e.target.value) || '' })}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Resolution</InputLabel>
              <Select
                value={editForm.video_resolution || '480p'}
                label="Resolution"
                onChange={(e) => setEditForm({ ...editForm, video_resolution: e.target.value })}
              >
                <MenuItem value="480p">480p</MenuItem>
                <MenuItem value="720p">720p</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Aspect Ratio</InputLabel>
              <Select
                value={editForm.video_aspect_ratio || '3:4'}
                label="Aspect Ratio"
                onChange={(e) => setEditForm({ ...editForm, video_aspect_ratio: e.target.value })}
              >
                {['3:4', '4:3', '1:1', '16:9', '9:16', '4:5', '3:2', '2:3'].map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <PromptField
              value={editForm.scene_analysis_system_prompt || ''}
              onChange={(val) => setEditForm({ ...editForm, scene_analysis_system_prompt: val })}
              label="SCENE ANALYSIS SYSTEM PROMPT"
              helperText="Sent to grok-4-1-fast-reasoning as the system message for scene analysis (Pass 1)"
              accentColor="#7D1935"
              minRows={14}
            />
          </Grid>
          <Grid item xs={12}>
            <PromptField
              value={editForm.video_generation_prompt || ''}
              onChange={(val) => setEditForm({ ...editForm, video_generation_prompt: val })}
              label="VIDEO GENERATION PROMPT"
              helperText="Universal video rules appended to the animation_prompt from Pass 1 for grok-imagine-video (Pass 2)"
              accentColor="#7D1935"
              minRows={8}
            />
          </Grid>
        </Grid>
      </Box>
    </AdminEditDialogShell>
  );

  const renderResetDialog = () => (
    <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
      <DialogTitle>Reset All Variations to Defaults?</DialogTitle>
      <DialogContent>
        <Typography>
          This will reset variations A and B to their defaults
          (Still & Tender, Alive & Moving).
          Any custom edits will be lost.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleResetDefaults} variant="contained" color="warning">
          Reset to Defaults
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderTestDialog = () => (
    <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Test Prompt Against Image</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Image URL"
              fullWidth
              placeholder="https://..."
              value={testImageUrl}
              onChange={(e) => setTestImageUrl(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Variation (optional)</InputLabel>
              <Select
                value={testVariationType}
                label="Variation (optional)"
                onChange={(e) => setTestVariationType(e.target.value)}
              >
                <MenuItem value="">Active Variation</MenuItem>
                <MenuItem value="A">A - Still & Tender</MenuItem>
                <MenuItem value="B">B - Alive & Moving</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleTest}
              disabled={!testImageUrl || testLoading}
              startIcon={testLoading ? <CircularProgress size={18} /> : <TestIcon />}
            >
              {testLoading ? 'Running Test...' : 'Run Test'}
            </Button>
          </Grid>
          {testResult && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="subtitle2" gutterBottom>Test Result</Typography>
                <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // ─── Main Render ───────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Grok Imagine Video — Prompt Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage the 2-pass video generation pipeline: scene analysis prompts (A/B), analytics, and testing.
      </Typography>

      <Box sx={{ mb: 3, border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
        <ModelDefaultsBar slots={['video', 'scene_analysis', 'scene_analysis_fallback']} />
      </Box>

      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 3 }}>
        <Tab label="Prompt Variations" />
        <Tab label="Analytics" />
        <Tab label="Scene Analysis Log" />
      </Tabs>

      {tabIndex === 0 && renderVariationsTab()}
      {tabIndex === 1 && renderAnalyticsTab()}
      {tabIndex === 2 && renderSceneLogTab()}

      {renderEditDialog()}
      {renderResetDialog()}
      {renderTestDialog()}
    </Box>
  );
};

export default GrokImaginePromptsManagement;
